import { useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import DraftService from '../services/DraftService';
import IDraftData from '../types/DraftData';

/**
 * Custom hook for managing flight range draft data
 * Follows Single Responsibility Principle - only handles draft data fetching and state
 */
export const useDrafts = () => {
  const [draftData, setDraftData] = useState<IDraftData[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchDraftsError, setFetchDraftsError] = useState<string | null>(null);
  const auth = useAuth();

  /**
   * Fetches flight range draft and sorts by modification date (latest first)
   * Maintains low cyclomatic complexity with clear error handling
   */
  const fetchDrafts = useCallback(async () => {
    try {
      setIsLoading(true);
      setFetchDraftsError(null);
      
      const token = auth.user?.access_token;
      if (!token) {
        throw new Error('Authentication token not found');
      }
      
      const response = await DraftService.getAll(token);
      
      // Sort by modification date descending (latest first)
      response.data.sort((a, b) => {
        const dateA = new Date(a.modifiedDate).getTime();
        const dateB = new Date(b.modifiedDate).getTime();
        return dateB - dateA; // Latest first
      });
      
      setDraftData(response.data);
    } catch (err) {
      const errorMessage = err instanceof Error && err.message === 'Authentication token not found'
        ? 'You must be authenticated to view flight range draft.'
        : 'Failed to load flight range draft. Please try again.';
      setFetchDraftsError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [auth]);

  return { 
    draftData, 
    isLoading, 
    fetchDraftsError, 
    fetchDrafts
  };
};